import React, { useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUploadAlt,
  faSearch,
  faSpinner,
  faTimes,
  faPercent,
  faDollarSign,
  faTag,
  faCheckCircle,
  faExclamationTriangle,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { duplicatesAPI, resolveFileUrl } from '../services/api';
import '../styles/DuplicateFinder.css';
import useI18n from '../hooks/useI18n';

interface ScanAnalysis {
  description: string;
  category: string;
  brand: string;
  model: string;
  condition: string;
  estimatedValue: { low: number; mid: number; high: number; currency: string };
  keywords: string[];
}

interface MatchResult {
  item: {
    id: string;
    name: string;
    description: string;
    condition: string;
    purchasePrice: number | null;
    purchaseCurrency: string;
    category: string | null;
    imageUrl: string | null;
  };
  score: number;
  matchReasons: string[];
  priceEstimate: { low: number; mid: number; high: number } | null;
}

interface ScanResult {
  scan: any;
  analysis: ScanAnalysis;
  matches: MatchResult[];
}

const DuplicateFinder: React.FC = () => {
  const { t } = useI18n();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      setError(t('duplicates.selectImage'));
      return;
    }
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
    setResult(null);
    setError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleScan = async () => {
    if (!selectedFile) return;
    setScanning(true);
    setError('');
    setResult(null);

    try {
      const res = await duplicatesAPI.scan(selectedFile);
      setResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const clearSelection = () => {
    if (preview) URL.revokeObjectURL(preview);
    setSelectedFile(null);
    setPreview('');
    setResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return '#ef4444';
    if (score >= 40) return '#f59e0b';
    return '#22c55e';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 70) return t('duplicates.highMatch');
    if (score >= 40) return t('duplicates.possibleMatch');
    return t('duplicates.lowMatch');
  };

  return (
    <div className="dup-finder">
      <div className="dup-header">
        <div className="dup-header-left">
          <h1>
            <FontAwesomeIcon icon={faSearch} />
            {t('duplicates.title')}
          </h1>
          <p>{t('duplicates.subtitle')}</p>
        </div>
      </div>

      <div className="dup-content">
        {/* Upload Area */}
        <div className="dup-upload-section">
          {!selectedFile ? (
            <div
              className={`dup-dropzone ${dragOver ? 'drag-over' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className="dup-dropzone-icon">
                <FontAwesomeIcon icon={faCloudUploadAlt} />
              </div>
              <h3>{t('duplicates.dropHere')}</h3>
              <p>{t('duplicates.supportedFormats')}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleInputChange}
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            <div className="dup-preview-area">
              <div className="dup-preview-wrapper">
                <img src={preview} alt="Selected" className="dup-preview-img" />
                <button className="dup-clear-btn" onClick={clearSelection}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="dup-preview-actions">
                <button
                  className="dup-scan-btn"
                  onClick={handleScan}
                  disabled={scanning}
                >
                  <FontAwesomeIcon icon={scanning ? faSpinner : faSearch} spin={scanning} />
                  {scanning ? t('duplicates.scanning') : t('duplicates.scanButton')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="dup-error">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            {error}
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="dup-results">
            {/* AI Analysis Card */}
            <div className="dup-analysis-card">
              <h3>{t('duplicates.aiAnalysis')}</h3>
              <div className="dup-analysis-grid">
                <div className="dup-analysis-item">
                  <label>{t('duplicates.description')}</label>
                  <p>{result.analysis.description}</p>
                </div>
                <div className="dup-analysis-row">
                  <div className="dup-analysis-item">
                    <label>{t('duplicates.category')}</label>
                    <span className="dup-tag">{result.analysis.category}</span>
                  </div>
                  <div className="dup-analysis-item">
                    <label>{t('duplicates.brand')}</label>
                    <span className="dup-tag">{result.analysis.brand}</span>
                  </div>
                  <div className="dup-analysis-item">
                    <label>{t('duplicates.model')}</label>
                    <span className="dup-tag">{result.analysis.model}</span>
                  </div>
                  <div className="dup-analysis-item">
                    <label>{t('duplicates.condition')}</label>
                    <span className="dup-tag">{result.analysis.condition}</span>
                  </div>
                </div>
                {result.analysis.estimatedValue && result.analysis.estimatedValue.mid > 0 && (
                  <div className="dup-value-estimate">
                    <label>{t('duplicates.estimatedValue')}</label>
                    <div className="dup-value-range">
                      <span className="dup-value-low">
                        ${result.analysis.estimatedValue.low.toLocaleString()}
                      </span>
                      <span className="dup-value-mid">
                        ${result.analysis.estimatedValue.mid.toLocaleString()}
                      </span>
                      <span className="dup-value-high">
                        ${result.analysis.estimatedValue.high.toLocaleString()}
                      </span>
                    </div>
                    <div className="dup-value-labels">
                      <span>{t('duplicates.low')}</span>
                      <span>{t('duplicates.mid')}</span>
                      <span>{t('duplicates.high')}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Matches */}
            <div className="dup-matches-section">
              <h3>
                {result.matches.length > 0 ? (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#6366f1' }} />
                    {t('duplicates.matchesFound').replace('{count}', String(result.matches.length))}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#22c55e' }} />
                    {t('duplicates.noDuplicates')}
                  </>
                )}
              </h3>

              {result.matches.length === 0 && (
                <p className="dup-no-matches">
                  {t('duplicates.noDuplicatesDesc')}
                </p>
              )}

              <div className="dup-matches-grid">
                {result.matches.map((match, index) => (
                  <div key={match.item.id} className="dup-match-card">
                    <div className="dup-match-header">
                      <span
                        className="dup-match-score"
                        style={{ background: getScoreColor(match.score) + '20', color: getScoreColor(match.score) }}
                      >
                        <FontAwesomeIcon icon={faPercent} />
                        {Math.round(match.score)}% — {getScoreLabel(match.score)}
                      </span>
                      <span className="dup-match-rank">#{index + 1}</span>
                    </div>
                    <div className="dup-match-body">
                      {match.item.imageUrl && (
                        <img
                          src={resolveFileUrl(match.item.imageUrl) || ''}
                          alt={match.item.name}
                          className="dup-match-img"
                        />
                      )}
                      <div className="dup-match-info">
                        <h4>{match.item.name}</h4>
                        {match.item.description && (
                          <p className="dup-match-desc">
                            {match.item.description.length > 120
                              ? match.item.description.substring(0, 117) + '...'
                              : match.item.description}
                          </p>
                        )}
                        <div className="dup-match-tags">
                          {match.item.category && (
                            <span className="dup-tag">
                              <FontAwesomeIcon icon={faTag} /> {match.item.category}
                            </span>
                          )}
                          <span className="dup-tag">{match.item.condition}</span>
                          {match.item.purchasePrice && (
                            <span className="dup-tag">
                              <FontAwesomeIcon icon={faDollarSign} />
                              {match.item.purchasePrice.toLocaleString()} {match.item.purchaseCurrency}
                            </span>
                          )}
                        </div>
                        <div className="dup-match-reasons">
                          {match.matchReasons.map((reason, i) => (
                            <span key={i} className="dup-reason">
                              <FontAwesomeIcon icon={faChevronRight} />
                              {reason}
                            </span>
                          ))}
                        </div>
                        {match.priceEstimate && (
                          <div className="dup-match-price">
                            <span>{t('duplicates.estValue')}</span>
                            <strong>
                              ${match.priceEstimate.low.toLocaleString()} — ${match.priceEstimate.high.toLocaleString()}
                            </strong>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DuplicateFinder;
